import { useState, useEffect } from "react";
import { format, parse, isValid } from "date-fns";
import { fr } from "date-fns/locale";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { CalendarDays } from "lucide-react";

// Sélecteur de date au format jj/mm/aaaa. value/onChange en ISO "yyyy-MM-dd".
// Mobile: input date natif (fiable au tactile). Desktop: calendrier personnalisé.
export const DateFieldFr = ({ value, onChange, testid = "date", placeholder = "jj/mm/aaaa", disablePast = true, className = "" }) => {
  const [open, setOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mq = window.matchMedia("(max-width: 639px)");
    const update = () => setIsMobile(mq.matches);
    update();
    mq.addEventListener("change", update);
    return () => mq.removeEventListener("change", update);
  }, []);

  const parsed = value ? parse(value, "yyyy-MM-dd", new Date()) : undefined;
  const valid = parsed && isValid(parsed);
  const todayIso = format(new Date(), "yyyy-MM-dd");

  if (isMobile) {
    return (
      <div className="relative">
        <CalendarDays className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
        <input
          type="date"
          value={value || ""}
          min={disablePast ? todayIso : undefined}
          onChange={(e) => onChange(e.target.value)}
          data-testid={testid}
          className={`w-full rounded-md border border-input bg-background pl-9 pr-3 text-sm outline-none focus:ring-2 focus:ring-ring appearance-none ${className || "h-10"}`}
        />
      </div>
    );
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button type="button" variant="outline" data-testid={testid}
          className={`w-full justify-start font-normal ${valid ? "" : "text-muted-foreground"} ${className}`}>
          <CalendarDays className="h-4 w-4 mr-2" />
          {valid ? format(parsed, "dd/MM/yyyy") : placeholder}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="single"
          locale={fr}
          selected={valid ? parsed : undefined}
          onSelect={(d) => { onChange(d ? format(d, "yyyy-MM-dd") : ""); setOpen(false); }}
          disabled={disablePast ? { before: new Date() } : undefined}
          initialFocus
        />
      </PopoverContent>
    </Popover>
  );
};

// Affichage d'une date ISO en jj/mm/aaaa (renvoie la valeur brute si non parsable).
export const formatDateFr = (iso) => {
  if (!iso) return "";
  const d = parse(iso, "yyyy-MM-dd", new Date());
  return isValid(d) ? format(d, "dd/MM/yyyy") : iso;
};

// Affichage d'un timestamp ISO complet en « jj/mm/aaaa à HH:mm » (date + heure).
export const formatDateTimeFr = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d)) return iso;
  return d.toLocaleString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
};
